import { ComponentInfo } from '@common/types';

import { WorkflowModeType } from '../../graphqlTypes/enums';
import { FunctionType } from '../../redux/types';
import { QueryKeys } from '../queries/common';
import { Gem } from '../queries/gems';
import { Entity } from '../types/Entity';
export type DropObjectType = { component: string; id?: string; repositoryPath?: string };

type EntityItem<E extends Entity> = {
  component: E;
  id: string;
  name: string;
  link: string;
  mode?: WorkflowModeType;
  repositoryPath?: string;
  path?: string;
};
export type BaseProjectEntityItem<E extends Entity> = DropObjectType & EntityItem<E>;
export type PipelineItem = BaseProjectEntityItem<Entity.Pipeline>;
export type PipelineConfiguration = BaseProjectEntityItem<Entity.PipelineConfiguration> & { configurationId: string };
type ConfigurationItem = BaseProjectEntityItem<Entity.Configuration> & {
  configurationInstance: {
    _id: string;
    name: string;
  };
};
export type DatasetItem = BaseProjectEntityItem<Entity.Dataset>;
type ModelItem = BaseProjectEntityItem<Entity.Model>;

export type SubgraphItem = BaseProjectEntityItem<Entity.Subgraph>;
type JobItem = BaseProjectEntityItem<Entity.Job>;
type ProjectItem = BaseProjectEntityItem<Entity.Project>;
export type FunctionItem = EntityItem<Entity.Function> & {
  type: FunctionType;
};
export interface FileItem {
  component: 'File';
  id: string;
  entityId: string;
  folder: string;
  icon: React.ReactNode;
}

export type SearchableGraphNodeItem = {
  id: string;
  name: string;
  graphPath: string;
  icon: React.ReactNode;
  graphNamePath?: string;
  component: string;
};
export interface TableItem extends DropObjectType {
  name: string;
  component: string;
  id: string;
  table: string;
  database: string;
  catalog?: string;
  isCatalogEnabled: boolean;
}
export interface GemItem extends DropObjectType {
  component: string;
  id: string;
  name: string;
  link: string;
  category: Gem['category'];
  description?: string;
  subCategory?: string;
  componentInfo?: ComponentInfo;
}

export type ProjectEntityTypes =
  | Entity.Pipeline
  | Entity.Project
  | Entity.PipelineConfiguration
  | Entity.Configuration
  | Entity.Dataset
  | Entity.Subgraph
  | Entity.Model
  | Entity.Function
  | Entity.Gem
  | Entity.Job;
type TableType = Entity.Table;
export type BaseEntityTypes = ProjectEntityTypes | TableType;
export type FileSystemEntityTypes = 'File' | 'Folder';
export type SearchableGraphNodeEntity = 'SearchableGraphNodeEntity';
export type EntityTypes = BaseEntityTypes | FileSystemEntityTypes | SearchableGraphNodeEntity;
export type EntityDataItem = {
  title: string;
  key: string;
  disabled?: boolean;
  isLeaf?: boolean;
  actions?: React.ReactNode;
  children: EntityType<EntityTypes>[];
};
export type EntityNodeItemType<T extends EntityTypes> = T extends Entity.Pipeline
  ? PipelineItem
  : T extends Entity.PipelineConfiguration
    ? PipelineConfiguration
    : T extends Entity.Configuration
      ? ConfigurationItem
      : T extends Entity.Dataset
        ? DatasetItem
        : T extends Entity.Subgraph
          ? ModelItem
          : T extends Entity.Model
            ? SubgraphItem
            : T extends Entity.Job
              ? JobItem
              : T extends Entity.Gem
                ? GemItem
                : T extends Entity.Function
                  ? FunctionItem
                  : T extends Entity.Project
                    ? ProjectItem
                    : T extends TableType
                      ? TableItem
                      : T extends 'File'
                        ? FileItem
                        : T extends SearchableGraphNodeEntity
                          ? SearchableGraphNodeItem
                          : never;

export type EntityType<T extends EntityTypes = EntityTypes> = {
  title: React.ReactNode;
  key: string;
  entity: T;
  loaded?: boolean;
  item: EntityNodeItemType<T>;
  nodeIcon: React.ReactNode;
  actions?: React.ReactNode | ((selected?: boolean) => React.ReactNode);
  info?: React.ReactNode;
  draggable?: boolean;
  disabled?: boolean;
  selected?: boolean;
  onClick?: () => void;
  children?: EntityType<EntityTypes>[];
  canDelete?: boolean;
};

export type GemEntityType = EntityType<Entity.Gem>;

export const ProjectBrowserQueryKeys = {
  ProjectBrowser: [QueryKeys.Git, QueryKeys.Project, 'ProjectBrowser'],
  ProjectBrowserCommitInfo: [QueryKeys.Git, QueryKeys.Project, 'ProjectBrowserCommitInfo'],
  SubgraphFiles: [QueryKeys.Git, QueryKeys.Project, 'SubgraphFile'],
  ProjectFiles: [QueryKeys.Git, QueryKeys.Project, 'ProjectFiles'],
  ProjectFunctionsFiles: [QueryKeys.Git, QueryKeys.Project, 'ProjectFunctionsFiles']
};
